import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  createdAt: string;
}

export function ApiKeysSection() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchKeys();
  }, []);

  async function fetchKeys() {
    try {
      setLoading(true);
      const response = await fetch("/api/api-keys", { credentials: "include" });
      if (!response.ok) {
        throw new Error("Failed to fetch API keys");
      }
      const data = await response.json();
      setKeys(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }

  async function createKey(e: React.FormEvent) {
    e.preventDefault();
    if (!newKeyName.trim()) return;

    try {
      setCreatingKey(true);
      setError(null);
      const response = await fetch("/api/api-keys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: newKeyName.trim() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to create API key");
      }

      const data = await response.json();
      setNewlyCreatedKey(data.data.key);
      setNewKeyName("");
      await fetchKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setCreatingKey(false);
    }
  }

  async function deleteKey(id: string) {
    try {
      setDeletingKeyId(id);
      setError(null);
      const response = await fetch(`/api/api-keys/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to delete API key");
      }

      await fetchKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setDeletingKeyId(null);
    }
  }

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard");
    }
  }

  function formatDate(dateString: string | null): string {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="material-symbols-outlined animate-spin text-muted-foreground">
          progress_activity
        </span>
        <span className="ml-2 text-muted-foreground">Loading API keys...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px]">error</span>
          {error}
        </div>
      )}

      {/* Newly created key banner */}
      {newlyCreatedKey && (
        <div className="p-4 rounded-lg bg-primary/10 border border-primary/30 space-y-3">
          <div className="flex items-center gap-2 text-primary font-medium">
            <span className="material-symbols-outlined text-[18px]">check_circle</span>
            API key created successfully
          </div>
          <p className="text-sm text-muted-foreground">
            Copy this key now. You won't be able to see it again!
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 p-2 rounded bg-background border border-border font-mono text-sm break-all">
              {newlyCreatedKey}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(newlyCreatedKey)}
            >
              <span className="material-symbols-outlined text-[18px]">
                {copied ? "check" : "content_copy"}
              </span>
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setNewlyCreatedKey(null)}
            className="text-muted-foreground"
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Create new key form */}
      <form onSubmit={createKey} className="flex items-end gap-3">
        <div className="flex-1 space-y-2">
          <label htmlFor="key-name" className="text-sm font-medium">
            Create new API key
          </label>
          <Input
            id="key-name"
            placeholder="Key name (e.g., Godot Editor - My Game)"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            disabled={creatingKey}
            maxLength={100}
          />
        </div>
        <Button type="submit" disabled={creatingKey || !newKeyName.trim()}>
          {creatingKey ? (
            <>
              <span className="material-symbols-outlined animate-spin text-[18px]">
                progress_activity
              </span>
              Creating...
            </>
          ) : (
            <>
              <span className="material-symbols-outlined text-[18px]">add</span>
              Create Key
            </>
          )}
        </Button>
      </form>

      {/* Keys list */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">
          Your API keys ({keys.length}/10)
        </h4>

        {keys.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No API keys yet. Create one to use with the Godot addon or API.
          </p>
        ) : (
          <div className="space-y-2">
            {keys.map((key) => (
              <div
                key={key.id}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
              >
                <div className="space-y-1">
                  <div className="font-medium">{key.name}</div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="font-mono">{key.prefix}...</span>
                    <span>Created: {formatDate(key.createdAt)}</span>
                    <span>Last used: {formatDate(key.lastUsedAt)}</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => deleteKey(key.id)}
                  disabled={deletingKeyId === key.id}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  {deletingKeyId === key.id ? (
                    <span className="material-symbols-outlined animate-spin text-[18px]">
                      progress_activity
                    </span>
                  ) : (
                    <span className="material-symbols-outlined text-[18px]">delete</span>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Usage instructions */}
      <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-2">
        <h4 className="text-sm font-medium flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px]">info</span>
          How to use API keys
        </h4>
        <p className="text-sm text-muted-foreground">
          Use your API key in the Godot addon settings or include it in API requests:
        </p>
        <code className="block p-2 rounded bg-background border border-border font-mono text-xs">
          Authorization: Bearer bckey_...
        </code>
      </div>
    </div>
  );
}
